import { isPlainObject } from '@reduxjs/toolkit';

import type {
  IPaginationArgs,
  TSortDirection,
} from 'interfaces/Pagination.interface';
import type { IPagination } from 'hooks/usePagination.hook';
import type { ISorting } from 'components/TableView/hooks/useSort.hook';
import type { ISimpleFilter } from 'hooks/useFilters.hook';
import {
  getIriData,
  isIriString,
  IRI_FILTER_KEYS,
} from 'helpers/iri/getIriData.helper';

export const createSortingApiArgs = (
  sorting?: ISorting
): { [key: string]: TSortDirection } => {
  const orderParams: { [key: string]: TSortDirection } = {};
  if (sorting && sorting.columnKey && sorting.direction) {
    const orderKey = `sort[${sorting.columnKey}]`;
    orderParams[orderKey] = sorting.direction;
  }
  return orderParams;
};

export const createPaginationApiArgs = (
  pagination: IPagination | false,
  sorting?: ISorting,
  overridePage?: number
): IPaginationArgs => {
  const orderParams = createSortingApiArgs(sorting);

  if (pagination === false) {
    return {
      pagination: false,
      order: orderParams,
    };
  }

  return {
    page: overridePage || pagination.currentPage,
    itemsPerPage: pagination.itemsPerPage,
    order: orderParams,
  };
};

// eslint-disable-next-line @typescript-eslint/no-explicit-any
const stripUndefined = (obj: any) => {
  if (!isPlainObject(obj)) {
    return obj;
  }
  const copy: Record<string, never> = { ...obj };

  for (const [k, v] of Object.entries(copy)) {
    if (typeof v === 'undefined') delete copy[k];
  }
  return copy;
};

export const serializeFlatObject = (
  paramObject: Record<string, string | boolean | number | undefined>,
  divider: '&' | '?' = '&'
): string => {
  const query = new URLSearchParams(stripUndefined(paramObject));
  return divider + query;
};

const checkValueIsBoolean = (value: unknown): value is boolean => {
  return typeof value === 'boolean';
};

const checkValueIsNumber = (value: unknown): value is number => {
  return typeof value === 'number';
};

const checkValueIsString = (value: unknown): value is string => {
  return typeof value === 'string';
};

// relation filter values are IRI strings anywhere internal state (Redux,
// saved ConfigPresets, FilterChips' external-option matching) is built or
// persisted, but the API only accepts the bare numeric id on the wire - so
// resolve here, once, right before serializing, rather than at every filter
// construction site. Exception: IRI_FILTER_KEYS/isIriFilter-marked filters
// (polymorphic relations like Log's 'about', Rating's 'target') need the
// full IRI preserved, since the bare id alone would lose the entity type.
const shouldPreserveIri = (filter: ISimpleFilter): boolean =>
  filter.isIriFilter === true || IRI_FILTER_KEYS.has(filter.key);

const resolveFilterValue = (filter: ISimpleFilter): ISimpleFilter['value'] => {
  const { value } = filter;
  if (shouldPreserveIri(filter)) {
    return value;
  }
  return typeof value === 'string' && isIriString(value)
    ? getIriData(value).id
    : value;
};

// date-range filter keys carry the inclusive boundary operator as a suffix
// (e.g. 'start[after]', 'end[before]') - this shape is relied on internally
// (FilterPanel/FilterChips pattern-match on 'before'/'after' to reconstruct
// from/to range UI state, and existing ConfigPresets are already persisted
// with it), so it's kept as the app's internal vocabulary. The API only
// accepts 'lte'/'gte' on the wire now, so the suffix is rewritten here, once,
// right before serializing. Renamed to the inclusive gte/lte (not the
// exclusive gt/lt also introduced by API Platform 4) to preserve before/
// after's original inclusive boundary semantics.
const BEFORE_KEY_SUFFIX = '[before]';
const AFTER_KEY_SUFFIX = '[after]';

const resolveFilterKey = (key: string): string => {
  if (key.endsWith(BEFORE_KEY_SUFFIX)) {
    return `${key.slice(0, -BEFORE_KEY_SUFFIX.length)}[lte]`;
  }
  if (key.endsWith(AFTER_KEY_SUFFIX)) {
    return `${key.slice(0, -AFTER_KEY_SUFFIX.length)}[gte]`;
  }
  return key;
};

// an 'empty' filter value is a UI-only sentinel (see mapResultByType.helper.ts's
// synthetic "no assignee" option) meaning "this relation is not set". The API
// no longer accepts it as a value on the field's own key - it must become a
// standalone exists[key]=false filter instead. Since exists[] has no OR/array
// form, any other filter sharing that key is dropped: "no assignee" and a
// specific-contact selection can't both apply to the same request.
const resolveEmptyValueFilters = (
  filters: ISimpleFilter[]
): ISimpleFilter[] => {
  const emptyValueKeys = new Set(
    filters
      .filter((filter) => filter.value === 'empty')
      .map((filter) => filter.key)
  );
  if (emptyValueKeys.size === 0) {
    return filters;
  }
  const remainingFilters = filters.filter(
    (filter) => !emptyValueKeys.has(filter.key)
  );
  const existsFilters: ISimpleFilter[] = Array.from(emptyValueKeys).map(
    (key) => ({
      key: `exists[${key}]`,
      operator: 'AND',
      value: false,
    })
  );
  return [...remainingFilters, ...existsFilters];
};

export const serializeFilters = (
  filters?: ISimpleFilter[],
  divider: '&' | '?' = '&'
): string => {
  if (!filters || filters.length === 0) {
    return '';
  }
  const resolvedFilters = resolveEmptyValueFilters(filters);
  let query = '';
  resolvedFilters.forEach((filter, index) => {
    if (index === 0) {
      query += divider;
    } else {
      query += '&';
    }
    query += resolveFilterKey(filter.key);
    switch (filter.operator) {
      case 'NOT':
        query += '[ne]=';
        break;
      case 'OR':
        query += '[]=';
        break;
      case 'AND':
      default:
        query += '=';
    }
    const value = resolveFilterValue(filter);
    if (
      checkValueIsString(value) ||
      checkValueIsBoolean(value) ||
      checkValueIsNumber(value)
    ) {
      query += encodeURIComponent(value);
    } else {
      query += value ? encodeURIComponent(value) : null;
    }
  });
  return query;
};
